import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import SiteMessage from 'models/SiteMessage'

import Form from 'pages/shared/Form'
import BrandFields from 'pages/shared/SiteMessageForm/BrandFields'
import RoleFields from 'pages/shared/SiteMessageForm/RoleFields'
import MessageTypeFields from 'pages/shared/SiteMessageForm/MessageTypeFields'
import SiteBannerFields from 'pages/shared/SiteMessageForm/SiteBannerFields'
import MessageFields from 'pages/shared/SiteMessageForm/MessageFields'
import ActionButtonFields from 'pages/shared/SiteMessageForm/ActionButtonFields'
import UpdatesPageFields from 'pages/shared/SiteMessageForm/UpdatesPageFields'

import { useStoreContext } from 'hooks/StoreContext'
import Api from 'services/api'

const propTypes = {
  title: PropTypes.string.isRequired,
  siteMessage: PropTypes.instanceOf(SiteMessage)
}

const defaultProps = {
  siteMessage: new SiteMessage()
}

const SiteMessageForm = ({ title, siteMessage }) => {
  const navigate = useNavigate()
  const { setAreSiteMessagesLoaded } = useStoreContext()

  const [siteMessageAttributes, setSiteMessageAttributes] = useState({ ...siteMessage })
  const [featureTours, setFeatureTours] = useState([])

  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (siteMessage.id != null) {
      Api.SiteMessages.toursForSiteMessage(siteMessage.id)
        .then((res) => {
          setFeatureTours(res)
        })
        .catch(() => {
          setFeatureTours([])
        })
    } else {
      setFeatureTours([])
    }
  }, [siteMessage.id]) //

  const compareArrays = (arr1, arr2) => {
    const set1 = new Set(arr1)
    const set2 = new Set(arr2)

    if (set1.size !== set2.size) {
      return false
    }

    for (const item of set1) {
      if (!set2.has(item)) {
        return false
      }
    }

    return true
  }

  const onSaveCallback = useCallback(() => {
    let featureConfirm = true
    const hasRoleChange = !compareArrays(siteMessageAttributes.roles, siteMessage.roles)
    const hasBrandChange = !compareArrays(siteMessageAttributes.brands, siteMessage.brands)
    if (featureTours.length > 0 && (hasRoleChange || hasBrandChange)) {
      const featureTourTitles = featureTours.map((tour) => tour.title).join(', ')
      const message = `Changing the message will remove the following feature tour(s): ${featureTourTitles}. Are you sure you want to change the message?`
      featureConfirm = confirm(message)
    }

    if (featureConfirm) {
      setIsSaving(true)

      const onSaveSuccessCallback = () => {
        // TODO: it would be nice for the user experience to stay on the edit page while both
        // deleting the record and refetching the list, then navigating to a list that instantly loads,
        // instead of navigating to the list and seeing a page spinner there.

        setAreSiteMessagesLoaded(false)
        navigate('/admin/site_messages')
      }

      const onValidationErrorCallback = (errors) => {
        setErrors(errors)

        setIsSaving(false)
      }

      const onSaveFailureCallBack = () => {
        setIsSaving(false)
        alert('Save Failed')
      }

      siteMessage.save(siteMessageAttributes, onSaveSuccessCallback, onSaveFailureCallBack, onValidationErrorCallback)
    }
  }, [navigate, siteMessage, siteMessageAttributes, setAreSiteMessagesLoaded, featureTours])

  const onCancelCallback = useCallback(() => {
    const hasChanges = !_.isEqual(siteMessageAttributes, { ...siteMessage })

    if (!hasChanges || (hasChanges && confirm('Are you sure you want to cancel?'))) {
      navigate('/admin/site_messages')
    }
  }, [navigate, siteMessage, siteMessageAttributes])

  const onDeleteCallback = useCallback(() => {
    if (confirm('Are you sure you want to delete this site message? This cannot be undone.')) {
      setIsDeleting(true)
      const onDeleteSuccessCallback = () => {
        // TODO: it would be nice for the user experience to stay on the edit page while both
        // deleting the record and refetching the list, then navigating to a list that instantly loads,
        // instead of navigating to the list and seeing a page spinner there.

        setAreSiteMessagesLoaded(false)
        navigate('/admin/site_messages')
      }

      const onDeleteFailureCallback = () => {
        setIsDeleting(false)
        alert('Delete Failed')
      }

      // TODO: change signature to accomodate named anonymous functions
      // siteMessage.delete({ onDeleteSuccessCallback: () => .., onDeleteFailureCallback: () => ...)
      siteMessage.delete(onDeleteSuccessCallback, onDeleteFailureCallback)
    }
  }, [navigate, siteMessage, setAreSiteMessagesLoaded])

  const onSiteMessageUpdateCallback = useCallback((updatedAttributes) => {
    setSiteMessageAttributes({ ...siteMessageAttributes, ...updatedAttributes })
  }, [siteMessageAttributes])

  return (
    <Form
      deleteButtonText="Message"
      title={title}
      isSaving={isSaving}
      isDeleting={isDeleting}
      displayDeleteButton={siteMessageAttributes.id !== null}
      onSaveCallback={onSaveCallback}
      onDeleteCallback={onDeleteCallback}
      onCancelCallback={onCancelCallback}
    >
      <BrandFields
        selectedBrands={siteMessageAttributes.brands}
        onChangeCallback={onSiteMessageUpdateCallback}
        errors={errors}
      />

      <RoleFields
        selectedRoles={siteMessageAttributes.roles}
        onChangeCallback={onSiteMessageUpdateCallback}
        errors={errors}
      />

      <MessageTypeFields
        messageType={siteMessageAttributes.messageType}
        onChangeCallback={onSiteMessageUpdateCallback}
      />

      <SiteBannerFields
        enabled={siteMessageAttributes.siteBannerEnabled}
        onChangeCallback={onSiteMessageUpdateCallback}
        errors={errors}
      />

      <UpdatesPageFields
        published={siteMessageAttributes.published}
        publishedAt={siteMessageAttributes.publishedAt}
        onChangeCallback={onSiteMessageUpdateCallback}
      />

      <MessageFields
        imageType={siteMessageAttributes.imageType}
        image={siteMessageAttributes.image}
        title={siteMessageAttributes.title}
        message={siteMessageAttributes.message}
        onChangeCallback={onSiteMessageUpdateCallback}
        errors={errors}
      />
      <br />
      <ActionButtonFields
        caption={siteMessageAttributes.actionButtonCaption}
        link={siteMessageAttributes.actionButtonLink}
        onChangeCallback={onSiteMessageUpdateCallback}
        errors={errors}
      />
    </Form>
  )
}

SiteMessageForm.propTypes = propTypes
SiteMessageForm.defaultProps = defaultProps

export default SiteMessageForm
